import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthProvider.js";
import { BrandLogo } from "../ui/BrandLogo.js";
import { Field, inputClass } from "./SignIn.js";

/**
 * Open pro sign-up (build-plan unit 15). Creates a Cognito account only —
 * portal capability is gated by the registry, so a fresh account lands in
 * the "application pending" experience until claiming / the in-portal
 * application (unit 16) links it to an APPROVED contractor record.
 *
 * Asks for the PERSON's name only. Business details (company, phone,
 * categories, service area) belong to the registry application, not Cognito.
 */
export function SignUp() {
  const { signUp, status } = useAuth();
  const navigate = useNavigate();

  const [givenName, setGivenName] = useState("");
  const [familyName, setFamilyName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (status === "signed-in") {
    return <Navigate to="/portal" replace />;
  }
  if (status === "loading") {
    return null;
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const { needsConfirmation } = await signUp({
        email,
        password,
        ...(givenName.trim() ? { givenName: givenName.trim() } : {}),
        ...(familyName.trim() ? { familyName: familyName.trim() } : {}),
      });
      if (needsConfirmation) {
        navigate(`/confirm?email=${encodeURIComponent(email)}`, { replace: true });
      } else {
        navigate("/sign-in", { replace: true, state: { from: "/portal" } });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Sign-up failed");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="mx-auto max-w-md px-6 py-10">
      <Link to="/" className="text-sm text-brand-700">
        &larr; Back
      </Link>
      <BrandLogo className="mx-auto mt-6" />
      <h1 className="mt-4 text-center text-2xl font-bold text-charcoal">Create your pro account</h1>
      <p className="mt-2 text-center text-sm text-charcoal">
        Track your job invitations, quotes, and messages in one place.
      </p>

      <form className="mt-8 space-y-5" onSubmit={onSubmit} noValidate>
        <div className="grid grid-cols-2 gap-4">
          <Field label="First name">
            <input
              type="text"
              name="given-name"
              value={givenName}
              onChange={(e) => setGivenName(e.target.value)}
              autoComplete="given-name"
              className={inputClass}
            />
          </Field>
          <Field label="Last name">
            <input
              type="text"
              name="family-name"
              value={familyName}
              onChange={(e) => setFamilyName(e.target.value)}
              autoComplete="family-name"
              className={inputClass}
            />
          </Field>
        </div>
        <Field label="Email">
          <input
            type="email"
            name="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            inputMode="email"
            autoComplete="email"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            required
            className={inputClass}
          />
        </Field>
        <Field label="Password">
          <div className="relative">
            <input
              type={showPassword ? "text" : "password"}
              name="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              required
              className={inputClass + " pr-16"}
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              className="absolute inset-y-0 right-3 my-auto h-8 px-2 text-sm font-medium text-brand-700"
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? "Hide" : "Show"}
            </button>
          </div>
          <p className="mt-1 text-xs text-charcoal">
            At least 8 characters with upper and lower case letters and a number.
          </p>
        </Field>

        {error && (
          <p role="alert" className="text-sm text-red-600">
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={submitting}
          className="inline-flex min-h-[44px] w-full items-center justify-center rounded-xl bg-brand-700 px-6 text-base font-semibold text-white shadow-sm active:bg-brand-800 disabled:opacity-60"
        >
          {submitting ? "Creating account…" : "Create account"}
        </button>
      </form>

      <p className="mt-6 text-sm text-charcoal">
        Already have an account?{" "}
        <Link to="/sign-in" className="font-semibold text-brand-700">
          Sign in
        </Link>
      </p>
    </main>
  );
}
